import { useEffect, useState, useRef } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { BeforeAfterBlogLayout } from "../before-after-blog-layout";
import { ProjectFilePreview, ProjectFilePreviewData } from "../project-file-preview";
import { MoodBoardPreview } from "../mood-board-preview";
import { useGetBeforeAndAfterProjectQuery } from "../../hooks/use-get-before-and-after-project-query";
import { useGetProjectFileQuery } from "../../hooks/use-get-project-file-query";
import { useGetMoodBoardQuery } from "../../hooks/use-get-mood-board-query";
import { PopulatedDesignBoard } from "../../__generated__/types";

interface ProjectPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  fileType: "project" | "before-after" | "mood-board" | null;
  fileId: string | null;
}

export const ProjectPreviewDialog: React.FC<ProjectPreviewDialogProps> = ({
  open,
  onClose,
  fileType,
  fileId,
}) => {
  const { getBeforeAndAfterProject, loading: loadingBeforeAfter } = useGetBeforeAndAfterProjectQuery();
  const { getProjectFile, loading: loadingProject } = useGetProjectFileQuery();
  const { getMoodBoard, loading: loadingMoodBoard } = useGetMoodBoardQuery();

  const [beforeAfterProject, setBeforeAfterProject] = useState<any>(null);
  const [projectFile, setProjectFile] = useState<ProjectFilePreviewData | null>(null);
  const [moodBoard, setMoodBoard] = useState<PopulatedDesignBoard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Use refs to store stable function references to prevent infinite loops
  const getBeforeAndAfterProjectRef = useRef(getBeforeAndAfterProject);
  const getProjectFileRef = useRef(getProjectFile);
  const getMoodBoardRef = useRef(getMoodBoard);

  // Update refs when functions change
  useEffect(() => {
    getBeforeAndAfterProjectRef.current = getBeforeAndAfterProject;
    getProjectFileRef.current = getProjectFile;
    getMoodBoardRef.current = getMoodBoard;
  }, [getBeforeAndAfterProject, getProjectFile, getMoodBoard]);

  useEffect(() => {
    if (!open || !fileId || !fileType) {
      setBeforeAfterProject(null);
      setProjectFile(null);
      setMoodBoard(null);
      setError(null);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (fileType === "before-after") {
          const project = await getBeforeAndAfterProjectRef.current(fileId, true);
          if (!isMounted) return;
          if (project) {
            setBeforeAfterProject(project);
          } else {
            setError("Project not found");
          }
        } else if (fileType === "project") {
          const project = await getProjectFileRef.current(fileId, true);
          if (!isMounted) return;
          if (project) {
            setProjectFile(project);
          } else {
            setError("Project not found");
          }
        } else if (fileType === "mood-board") {
          const board = await getMoodBoardRef.current(fileId, true);
          if (!isMounted) return;
          if (board) {
            setMoodBoard(board);
          } else {
            setError("Design board not found");
          }
        }
      } catch (err) {
        if (!isMounted) return;
        setError("Failed to load project. Please try again.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [open, fileId, fileType]);

  const isLoading = loading || loadingBeforeAfter || loadingProject || loadingMoodBoard;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          margin: 2,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {fileType === "before-after" && "Before & After Project"}
          {fileType === "project" && "Project File"}
          {fileType === "mood-board" && "Design Board"}
          {!fileType && "Project Preview"}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 3,
          backgroundColor: "#F5F5F5",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress sx={{ color: "#00A06D" }} />
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 400,
            }}
          >
            <Typography variant="body1" color="error">
              {error}
            </Typography>
          </Box>
        ) : fileType === "before-after" && beforeAfterProject ? (
          <BeforeAfterBlogLayout
            project={{
              _id: beforeAfterProject._id,
              name: beforeAfterProject.name,
              storyTitle: beforeAfterProject.storyTitle,
              storySubtitle: beforeAfterProject.storySubtitle,
              introduction: beforeAfterProject.introduction,
              challenges: beforeAfterProject.challenges,
              wishList: beforeAfterProject.wishList,
              overview: beforeAfterProject.overview,
              additionalSections: beforeAfterProject.additionalSections,
              beforeImages: beforeAfterProject.beforeImages?.map((img: any) => ({
                _id: img._id,
                url: img.url,
                title: img.title,
                description: img.description,
              })),
              afterImages: beforeAfterProject.afterImages?.map((img: any) => ({
                _id: img._id,
                url: img.url,
                title: img.title,
                description: img.description,
              })),
              coverImage: beforeAfterProject.coverImage
                ? {
                    _id: beforeAfterProject.coverImage._id,
                    url: beforeAfterProject.coverImage.url,
                  }
                : null,
              style: beforeAfterProject.style,
              projectCategory: beforeAfterProject.projectCategory,
              budgetRange: beforeAfterProject.budgetRange,
              squareFootage: beforeAfterProject.squareFootage,
              projectDuration: beforeAfterProject.projectDuration,
              specialRequirements: beforeAfterProject.specialRequirements,
              designerId: beforeAfterProject.designerId ? {
                _id: beforeAfterProject.designerId._id,
                firstName: beforeAfterProject.designerId.firstName,
                lastName: beforeAfterProject.designerId.lastName,
                businessCity: beforeAfterProject.designerId.businessCity,
                businessState: beforeAfterProject.designerId.businessState,
              } : null,
            }}
            showEditButton={false}
            isMember={true}
            showPaywall={false}
            onBack={onClose}
          />
        ) : fileType === "project" && projectFile ? (
          <ProjectFilePreview
            project={projectFile}
            showConnectButton={false}
          />
        ) : fileType === "mood-board" && moodBoard ? (
          <MoodBoardPreview designBoard={moodBoard} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
